import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../api/authApi';

const initialErrorState = { error: false, message: '' };

function isValidName(value) {
  return value.trim() !== '' && !/\d/.test(value);
}

function isValidUsername(value) {
  return value.trim() !== '' && !/\s/.test(value);
}

function isValidPassword(value) {
  return value.trim() !== '' && value.trim().length >= 8 && value.trim().length <= 16 && !/\s/.test(value);
}

function isValidEmail(value) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
}

function getProfessionalCode(value) {
  if (value === 'Enfermero/a') {
    return 1;
  }

  if (value === 'Fisioterapeuta') {
    return 2;
  }

  return 0;
}

export default function RegisterForm() {
  const navigate = useNavigate();
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [usuario, setUsuario] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [profesional, setProfesional] = useState('');
  const [loading, setLoading] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [openBad, setOpenBad] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Registro completado. Tu cuenta esta pendiente de aprobacion por un administrador.');
  const [errorMessage, setErrorMessage] = useState('No se pudo completar el registro.');
  const [errorNombre, setErrorNombre] = useState(initialErrorState);
  const [errorApellidos, setErrorApellidos] = useState(initialErrorState);
  const [errorUsuario, setErrorUsuario] = useState(initialErrorState);
  const [errorPassword, setErrorPassword] = useState(initialErrorState);
  const [errorEmail, setErrorEmail] = useState(initialErrorState);

  const buttonStyle = useMemo(() => ({ backgroundColor: '#0D6AEE' }), []);

  const validateForm = () => {
    let hasError = false;

    if (!isValidName(nombre)) {
      setErrorNombre({ error: true, message: 'El nombre no puede tener numeros' });
      hasError = true;
    } else {
      setErrorNombre(initialErrorState);
    }

    if (!isValidName(apellidos)) {
      setErrorApellidos({ error: true, message: 'Los apellidos no pueden tener numeros' });
      hasError = true;
    } else {
      setErrorApellidos(initialErrorState);
    }

    if (!isValidUsername(usuario)) {
      setErrorUsuario({ error: true, message: 'El usuario no puede tener espacios en blanco' });
      hasError = true;
    } else {
      setErrorUsuario(initialErrorState);
    }

    if (!isValidPassword(password)) {
      setErrorPassword({ error: true, message: 'La contrasena debe tener entre 8 y 16 caracteres' });
      hasError = true;
    } else {
      setErrorPassword(initialErrorState);
    }

    if (!isValidEmail(email)) {
      setErrorEmail({ error: true, message: 'Introduce un email valido' });
      hasError = true;
    } else {
      setErrorEmail(initialErrorState);
    }

    return !hasError;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setOpenBad(true);
      return;
    }

    setLoading(true);
    try {
      const response = await registerUser({
        username: usuario,
        firstname: nombre,
        lastname: apellidos,
        email,
        password,
        profesional: getProfessionalCode(profesional),
        is_admin: false,
      });

      setSuccessMessage(response.message || 'Registro completado. Tu cuenta esta pendiente de aprobacion por un administrador.');
      setOpenGood(true);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'No se pudo completar el registro.');
      setOpenBad(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Paper elevation={20} sx={{ p: 2, width: 400 }}>
        <Grid align="center">
          <Avatar sx={{ backgroundColor: '#0D6AEE' }}>
            <AddCircleOutlineIcon />
          </Avatar>
          <Typography variant="h5">Registrarse</Typography>
        </Grid>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Nombre" required fullWidth margin="normal" error={errorNombre.error} helperText={errorNombre.message} value={nombre} onChange={(event) => setNombre(event.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Apellidos" required fullWidth margin="normal" error={errorApellidos.error} helperText={errorApellidos.message} value={apellidos} onChange={(event) => setApellidos(event.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Usuario" required fullWidth margin="normal" error={errorUsuario.error} helperText={errorUsuario.message} value={usuario} onChange={(event) => setUsuario(event.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" required fullWidth margin="normal" error={errorEmail.error} helperText={errorEmail.message} value={email} onChange={(event) => setEmail(event.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Contrasena" required type="password" error={errorPassword.error} helperText={errorPassword.message} fullWidth margin="normal" value={password} onChange={(event) => setPassword(event.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="register-profession-label">Profesion</InputLabel>
                <Select labelId="register-profession-label" label="Profesion" value={profesional} onChange={(event) => setProfesional(event.target.value)}>
                  <MenuItem value="Enfermero/a">Enfermero/a</MenuItem>
                  <MenuItem value="Fisioterapeuta">Fisioterapeuta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" style={buttonStyle} disabled={loading}>
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Registrarse'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                Ya te has registrado?{' '}
                <Link
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    navigate('/login');
                  }}
                >
                  Iniciar sesion aqui
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>

        <Snackbar
          open={openGood}
          autoHideDuration={3500}
          onClose={() => setOpenGood(false)}
        >
          <Alert
            severity="success"
            onClose={() => setOpenGood(false)}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/login')}>
                Ir a login
              </Button>
            }
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <Snackbar open={openBad} autoHideDuration={2000} onClose={() => setOpenBad(false)}>
          <Alert severity="error" onClose={() => setOpenBad(false)}>{errorMessage}</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}
